import React, { useEffect, useState } from "react";
import {
  Breadcrumb,
  Card,
  Row,
  Col,
  Table,
  Spinner,
  Button,
} from "react-bootstrap";
import useReactRouter from "use-react-router";
import moment from "moment";
import { useLazyQuery } from "@apollo/client";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import {
  FaEye,
  FaStore,
  FaBoxOpen,
  FaCalendarDay,
  FaUsers,
  FaStar,
  FaMapMarkerAlt,
  FaWhatsapp,
  FaFacebook,
  FaGlobe,
} from "react-icons/fa";
import { MdDateRange } from "react-icons/md";
import { Helmet } from "react-helmet";

import { COLOR_APP } from "../../constants";
import {
  imageRender,
  thousandSeperate,
  numberFormat,
  convertCurrentDate,
} from "../../helpers";
import { SHOP_VIEW_REPORT } from "../../graphql/view/query";
import { SHOP } from "../../graphql/shop/queryShop";
import {
  GET_USER_FOLLOWERS,
  GET_SHOP_SOLD_ORDER_COUNT,
  GET_SHOP_COMMENTS,
} from "../../graphql/shopStats/query";
import CurrentDateModal from "../../components/currentComponent/CurrentDateModal";

//* ໜ້າ "ຕິດຕາມການເຂົ້າຊົມ" — ຍອດເຂົ້າຊົມຮ້ານ/ສິນຄ້າ + ຕາມເວລາ (ເລືອກຊ່ວງໄດ້)
export default function ViewStats() {
  const { history } = useReactRouter();
  const SHOP_ID = localStorage.getItem("SHOP");

  //* ຊ່ວງເວລາ — ຮູບແບບດຽວກັບ Dashboard (cdFillter + CurrentDateModal). default: ເດືອນປັດຈຸບັນ
  const [cdFillter, setCdFillter] = useState({
    createdAt_gte: moment().startOf("month").format("YYYY-MM-DD"),
    createdAt_lt: moment().endOf("month").format("YYYY-MM-DD"),
  });
  const [activeButton, setActiveButton] = useState("thisMonth");
  const [isCurrentModals, setIsCurrentModals] = useState(false);

  const [getReport, { data, loading }] = useLazyQuery(SHOP_VIEW_REPORT, {
    fetchPolicy: "network-only",
  });

  //* ຂໍ້ມູນຮ້ານ + ສະຖິຕິ (ຜູ້ຕິດຕາມ / ຂາຍແລ້ວ / ຣີວິວ) — UI ຄືກັບໜ້າ about-us ຂອງ sale page
  const [shopData, setShopData] = useState();
  const [followerCount, setFollowerCount] = useState(0);
  const [totalSold, setTotalSold] = useState(null);
  const [reviewStats, setReviewStats] = useState({ average: 0, count: 0 });

  const [getShop, { data: loadShopData }] = useLazyQuery(SHOP, {
    fetchPolicy: "cache-and-network",
  });
  const [getShopFollowers] = useLazyQuery(GET_USER_FOLLOWERS, {
    fetchPolicy: "network-only",
  });
  const [getSoldOrderCount] = useLazyQuery(GET_SHOP_SOLD_ORDER_COUNT, {
    fetchPolicy: "cache-and-network",
  });
  const [getShopComments] = useLazyQuery(GET_SHOP_COMMENTS, {
    fetchPolicy: "cache-and-network",
  });

  useEffect(() => {
    if (!SHOP_ID) return;
    getShop({ variables: { where: { id: SHOP_ID } } });
    getShopStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [SHOP_ID]);

  useEffect(() => {
    if (loadShopData?.shop) setShopData(loadShopData.shop);
  }, [loadShopData]);

  //* ດຶງສະຖິຕິ — ແຕ່ລະອັນ try/catch ແຍກກັນ ຖ້າອັນໜຶ່ງພັງ ອັນອື່ນຍັງຂຶ້ນ
  const getShopStats = async () => {
    try {
      const res = await getShopFollowers({
        variables: { where: { followedShop: SHOP_ID }, limit: 1 },
      });
      setFollowerCount(res?.data?.userFollowers?.total || 0);
    } catch (error) {
      console.log("followers error: ", error);
    }

    try {
      const res = await getSoldOrderCount({
        variables: {
          where: {
            shop: SHOP_ID,
            isDeleted: false,
            type: "SALE_PAGE",
            status: ["COMPLETED", "SHIPPINGCOMPLETED", "SHIPPING_COMPLETED"],
          },
          limit: 1,
          skip: 0,
        },
      });
      setTotalSold(res?.data?.orderGroups?.total ?? null);
    } catch (error) {
      console.log("totalSold error: ", error);
    }

    //* ຣີວິວຮ້ານ — ຄັດເອົາສະເພາະ comment ທີ່ບໍ່ມີ product (review ຮ້ານ ບໍ່ແມ່ນ review ສິນຄ້າ)
    try {
      const res = await getShopComments({
        variables: { where: { shop: SHOP_ID } },
      });
      const all = res?.data?.comments?.data || [];
      const reviews = all.filter((c) => !c.product && !c.productVariant);
      if (reviews.length > 0) {
        const sum = reviews.reduce((s, c) => s + (Number(c.rate) || 0), 0);
        setReviewStats({
          average: sum / reviews.length,
          count: reviews.length,
        });
      } else {
        setReviewStats({ average: 0, count: 0 });
      }
    } catch (error) {
      console.log("reviews error: ", error);
    }
  };

  const shopAddress = [
    shopData?.address?.village && `ບ້ານ ${shopData.address.village}`,
    shopData?.address?.district && `ເມືອງ ${shopData.address.district}`,
    shopData?.address?.province,
  ]
    .filter(Boolean)
    .join(", ");

  const openWhatsApp = () => {
    const url = `https://wa.me/+856020${shopData?.phone}?text=${encodeURIComponent(
      "ສະບາຍດີ🙏",
    )}`;
    window.open(url, "_blank");
  };

  const shopStats = [
    {
      key: "followers",
      Icon: FaUsers,
      label: "ຜູ້ຕິດຕາມ",
      value: numberFormat(followerCount),
    },
    {
      key: "sold",
      Icon: FaBoxOpen,
      label: "ຂາຍແລ້ວ",
      value: totalSold === null ? "-" : numberFormat(totalSold),
    },
    {
      key: "rating",
      Icon: FaStar,
      label: "ຣີວິວ",
      value: reviewStats.count > 0 ? reviewStats.average.toFixed(1) : "-",
    },
  ];

  //* refetch ອັດຕະໂນມັດ ຕອນ cdFillter ປ່ຽນ (modal ເລືອກວັນທີ → set cdFillter → refetch)
  useEffect(() => {
    if (!SHOP_ID) return;
    getReport({
      variables: {
        shop: SHOP_ID,
        topLimit: 10,
        //* ກວມທັງມື້: gte 00:00, lt 23:59:59
        startDate: moment(cdFillter.createdAt_gte).startOf("day").toISOString(),
        endDate: moment(cdFillter.createdAt_lt).endOf("day").toISOString(),
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cdFillter, SHOP_ID]);

  const report = data?.shopViewReport;

  //* ຂໍ້ມູນ chart — ຕື່ມມື້ທີ່ບໍ່ມີ view ໃຫ້ເປັນ 0 (ໃຫ້ chart ຕໍ່ເນື່ອງ)
  const chartData = (() => {
    if (!report?.byDay) return [];
    const map = {};
    report.byDay.forEach((d) => (map[d.date] = d.count));
    const out = [];
    const cur = moment(cdFillter.createdAt_gte);
    const end = moment(cdFillter.createdAt_lt);
    let guard = 0;
    while (cur.isSameOrBefore(end) && guard < 400) {
      const key = cur.format("YYYY-MM-DD");
      out.push({ date: cur.format("DD/MM"), count: map[key] || 0 });
      cur.add(1, "day");
      guard++;
    }
    return out;
  })();

  const cards = [
    {
      key: "shopTotal",
      label: "ເຂົ້າຊົມຮ້ານ (ທັງໝົດ)",
      value: report?.shopTotal,
      Icon: FaStore,
      color: COLOR_APP,
    },
    // {
    //   key: "rangeTotal",
    //   label: "ເຂົ້າຊົມຮ້ານ (ຊ່ວງທີ່ເລືອກ)",
    //   value: report?.rangeTotal,
    //   Icon: FaCalendarDay,
    //   color: "#00a86b",
    // },
    {
      key: "today",
      label: "ເຂົ້າຊົມ ມື້ນີ້",
      value: report?.today,
      Icon: FaEye,
      color: "#f0932b",
    },
    {
      key: "productViewsTotal",
      label: "ເບິ່ງສິນຄ້າ ",
      value: report?.productViewsTotal,
      Icon: FaBoxOpen,
      color: "#8e44ad",
    },
  ];

  return (
    <div style={{ padding: "0 8px" }}>
      <Helmet>
        <title>ຕິດຕາມການເຂົ້າຊົມ</title>
      </Helmet>

      <Breadcrumb>
        <Breadcrumb.Item onClick={() => history.replace("/settings")}>
          ຕັ້ງຄ່າ
        </Breadcrumb.Item>
        <Breadcrumb.Item active>ຕິດຕາມການເຂົ້າຊົມ</Breadcrumb.Item>
      </Breadcrumb>

      {/* shop detail  */}
      <Card className="mb-3 border-0 shadow-sm" style={{ overflow: "hidden" }}>
        {/* cover */}
        <div
          style={{
            height: 90,
            background: "linear-gradient(45deg,#0e2a47 0%,#00c0d0 100%)",
          }}
        />

        <Card.Body className="pt-0 text-center">
          {/* ຮູບຮ້ານ — ຊ້ອນຂຶ້ນທັບ cover */}
          <img
            src={imageRender(shopData?.image)}
            alt={shopData?.name || ""}
            onError={(e) => {
              e.target.src = "/images/noPhoto.png";
            }}
            style={{
              width: 96,
              height: 96,
              borderRadius: "50%",
              objectFit: "cover",
              border: "4px solid #fff",
              background: "#f2f2f2",
              boxShadow: "0 2px 6px rgba(0,0,0,.15)",
              marginTop: -52,
            }}
          />

          <h5
            style={{ fontWeight: 700, color: "#333", marginTop: 12 }}
            className="mb-0"
          >
            {shopData?.name || "-"}
          </h5>

          {shopAddress ? (
            <div
              className="d-flex align-items-center justify-content-center mt-2"
              style={{ gap: 6, fontSize: 13, color: "#888" }}
            >
              <FaMapMarkerAlt style={{ flexShrink: 0 }} />
              <span>{shopAddress}</span>
            </div>
          ) : null}

          {/* ປຸ່ມຕິດຕໍ່ */}
          <div
            className="d-flex flex-wrap justify-content-center mt-3"
            style={{ gap: 8 }}
          >
            {shopData?.phone ? (
              <Button
                size="sm"
                variant="outline-success"
                onClick={openWhatsApp}
                className="d-flex align-items-center"
                style={{ gap: 6 }}
              >
                <FaWhatsapp size={16} />
                +856 20 {shopData.phone}
              </Button>
            ) : null}
            {shopData?.facebookUrl ? (
              <Button
                size="sm"
                variant="outline-primary"
                onClick={() => window.open(shopData.facebookUrl, "_blank")}
                className="d-flex align-items-center"
                style={{ gap: 6 }}
              >
                <FaFacebook size={16} />
                Facebook
              </Button>
            ) : null}
            {shopData?.website ? (
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={() => window.open(shopData.website, "_blank")}
                className="d-flex align-items-center"
                style={{ gap: 6 }}
              >
                <FaGlobe size={16} />
                ເວັບໄຊ້
              </Button>
            ) : null}
          </div>

          {/* ສະຖິຕິ — ຜູ້ຕິດຕາມ / ຂາຍແລ້ວ / ຣີວິວ */}
          <Row className="mt-3">
            {shopStats.map((s) => (
              <Col xs={4} key={s.key}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 4,
                    border: "1px solid #eee",
                    borderRadius: 10,
                    padding: "12px 6px",
                    height: "100%",
                  }}
                >
                  {s.key === "rating" ? (
                    <div style={{ display: "flex", gap: 2 }}>
                      {[1, 2, 3, 4, 5].map((n) => (
                        <FaStar
                          key={n}
                          size={14}
                          color={
                            n <= Math.round(reviewStats.average)
                              ? "#fbbf24"
                              : "#d1d5db"
                          }
                        />
                      ))}
                    </div>
                  ) : (
                    <s.Icon size={18} color={COLOR_APP} />
                  )}
                  <span
                    style={{ fontSize: 16, fontWeight: 700, color: "#333" }}
                  >
                    {s.value}
                  </span>
                  <span style={{ fontSize: 11, color: "#9ca3af" }}>
                    {s.label}
                  </span>
                </div>
              </Col>
            ))}
          </Row>
        </Card.Body>
      </Card>

      {/* ເລືອກຊ່ວງເວລາ */}
      <Card className="mb-3 border-0 shadow-sm">
        <Card.Body>
          <div className="d-flex align-items-center" style={{ gap: 8 }}>
            <span style={{ fontSize: 13, color: "#666", whiteSpace: "nowrap" }}>
              ຊ່ວງເວລາ:
            </span>
            <Button
              variant="outline-secondary"
              onClick={() => setIsCurrentModals(true)}
              style={{
                width: "100%",
                maxWidth: 260,
                display: "flex",
                gap: 5,
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              {loading ? (
                <Spinner size="sm" />
              ) : (
                <MdDateRange style={{ fontSize: 22 }} />
              )}

              {cdFillter?.createdAt_gte === "" &&
              cdFillter?.createdAt_lt === "" ? (
                "mm/dd/yyyy"
              ) : (
                <>
                  {convertCurrentDate(cdFillter?.createdAt_gte) +
                    " - " +
                    convertCurrentDate(cdFillter?.createdAt_lt)}
                </>
              )}
            </Button>
          </div>
        </Card.Body>
      </Card>

      {loading && !report ? (
        <div className="text-center py-5">
          <Spinner animation="border" style={{ color: COLOR_APP }} />
        </div>
      ) : (
        <>
          {/* summary cards */}
          <Row>
            {cards.map((c, index) => (
              <Col
                xs={
                  cards.length % 2 === 1 && index === cards.length - 1 ? 12 : 6
                }
                md={Math.max(3, Math.floor(12 / cards.length))}
                key={c.key}
                className="mb-3"
              >
                <Card className="border-0 shadow-sm h-100">
                  <Card.Body className="d-flex align-items-center">
                    <div
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: 12,
                        background: `${c.color}20`,
                        color: c.color,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginRight: 12,
                        flexShrink: 0,
                      }}
                    >
                      <c.Icon size={20} />
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 22,
                          fontWeight: 700,
                          lineHeight: 1.1,
                          color: "#333",
                        }}
                      >
                        {thousandSeperate(c.value || 0)}
                      </div>
                      <div style={{ fontSize: 12, color: "#888" }}>
                        {c.label}
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          {/* chart ຍอดเข้าชมต่อวัน */}
          <Card className="border-0 shadow-sm mb-3">
            <Card.Body>
              <h6 style={{ fontWeight: 700, color: "#333" }} className="mb-3">
                ຍອດເຂົ້າຊົມຮ້ານ ຕໍ່ວັນ
              </h6>
              {chartData.length === 0 ? (
                <div className="text-center text-muted py-4">
                  ຍັງບໍ່ມີຂໍ້ມູນ
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart
                    data={chartData}
                    margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient
                        id="viewGradient"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor={COLOR_APP}
                          stopOpacity={0.35}
                        />
                        <stop
                          offset="95%"
                          stopColor={COLOR_APP}
                          stopOpacity={0}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 11 }}
                      interval="preserveStartEnd"
                    />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip
                      formatter={(v) => [thousandSeperate(v), "ເຂົ້າຊົມ"]}
                    />
                    <Area
                      type="monotone"
                      dataKey="count"
                      stroke={COLOR_APP}
                      strokeWidth={2.5}
                      fill="url(#viewGradient)"
                      dot={{ r: 3, fill: COLOR_APP, strokeWidth: 0 }}
                      activeDot={{ r: 5 }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </Card.Body>
          </Card>

          {/* top products */}
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <h6 style={{ fontWeight: 700, color: "#333" }} className="mb-3">
                ສິນຄ້າຖືກເບິ່ງຫຼາຍສຸດ
              </h6>
              {!report?.topProducts || report.topProducts.length === 0 ? (
                <div className="text-center text-muted py-4">
                  ຍັງບໍ່ມີການເບິ່ງສິນຄ້າ
                </div>
              ) : (
                <Table responsive hover className="mb-0 align-middle">
                  <thead>
                    <tr style={{ fontSize: 13, color: "#888" }}>
                      <th style={{ width: 50 }}>#</th>
                      <th>ສິນຄ້າ</th>
                      <th className="text-end" style={{ width: 120 }}>
                        ຈຳນວນເບິ່ງ
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.topProducts.map((p, i) => (
                      <tr key={p.id}>
                        <td className="text-muted">{i + 1}</td>
                        <td>
                          <div className="d-flex align-items-center">
                            <img
                              src={imageRender(p.image)}
                              alt=""
                              style={{
                                width: 40,
                                height: 40,
                                borderRadius: 8,
                                objectFit: "cover",
                                marginRight: 10,
                                background: "#f2f2f2",
                              }}
                              onError={(e) => {
                                e.target.src = "/images/noPhoto.png";
                              }}
                            />
                            <span style={{ fontSize: 14 }}>
                              {p.name || "-"}
                            </span>
                          </div>
                        </td>
                        <td className="text-end">
                          <span
                            style={{
                              fontWeight: 700,
                              color: COLOR_APP,
                            }}
                          >
                            {thousandSeperate(p.viewCount)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </>
      )}

      {/* modal ເລືອກຊ່ວງເວລາ — ຮູບແບບດຽວກັບ Dashboard */}
      <CurrentDateModal
        openModal={isCurrentModals}
        onCloseModal={() => setIsCurrentModals(false)}
        setCdFillter={setCdFillter}
        setActiveButton={setActiveButton}
        activeButton={activeButton}
        backdrop={true}
      />
    </div>
  );
}
